import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ITranslatable, TranslatableParam } from '@shared/interfaces/translatable.interface';
import { Subscription } from 'rxjs';

// Renders an ITranslatable ({ key, params }) through TranslateService,
// resolving nested translatable params first so a sentence can embed
// translated fragments. Plain strings pass through untouched, so a column
// can mix legacy text and translatable cells.
//
// Impure, like ngx-translate's own pipe, so the output follows language and
// translation-file changes; the last result is cached per input reference.
@Pipe({ name: 'translatable', pure: false, standalone: false })
export class TranslatablePipe implements PipeTransform, OnDestroy {
  private lastInput: unknown;
  private lastValue = '';
  private lastVersion = -1;
  private version = 0;
  private readonly subscriptions: Subscription[];

  constructor(
    private readonly _translate: TranslateService,
    private readonly _cdr: ChangeDetectorRef
  ) {
    this.subscriptions = [
      this._translate.onLangChange,
      this._translate.onTranslationChange,
      this._translate.onDefaultLangChange
    ].map((event) =>
      event.subscribe(() => {
        this.version++;
        this._cdr.markForCheck();
      })
    );
  }

  transform(value: TranslatableParam | null | undefined): string {
    if (value === this.lastInput && this.version === this.lastVersion) {
      return this.lastValue;
    }

    this.lastInput = value;
    this.lastVersion = this.version;
    this.lastValue = this.resolve(value);

    return this.lastValue;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private resolve(value: TranslatableParam | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value !== 'object') {
      return String(value);
    }

    if (!value.key) {
      return '';
    }

    const params: { [name: string]: string } = {};
    Object.entries(value.params ?? {}).forEach(([name, param]) => {
      params[name] = this.resolve(param);
    });

    return this._translate.instant(value.key, params);
  }
}
